import {hash, GlobalHash} from "@/prototype/utils/Hash.js";
import Error              from "@/prototype/utils/Error.js";

import deterministic_stringify from "@/library/stringify.js";

import global from "@/prototype/global.js";

import PQueue from "p-queue/dist/index.js";

const log = global.log.getLogger("f_mtac.storage.space.context.object_index");
log.setLevel(log.levels.WARN);

// object method
const OBJECT_CREATE = 11;
const OBJECT_DELETE = 12;
const OBJECT_UPDATE = 13;
const OBJECT_IGNORE = 14;
// changeset state
const CHANGESET_PENDING  = 21;
const CHANGESET_WAITING  = 22;
const CHANGESET_PUSHED   = 23;
const CHANGESET_REVERTED = 24;

let changeset_prototype = {
    store: function() {
        this.method = OBJECT_CREATE;
    },
    delete: function() {
        if (this.method === OBJECT_CREATE) {
            this.method = OBJECT_IGNORE;
            return;
        }
        this.method = OBJECT_DELETE;
    },
    update: function() {
        if (this.method === OBJECT_CREATE) {
            return;
        }
        this.method = OBJECT_UPDATE;
    }
};
let Changeset = function(local_id) {
    this.method = null;
    this.state = CHANGESET_PENDING;
    this.old_data = null;
    this.old_hash = null;
    this.new_data = null;
    this.new_hash = null;
    this.global_hash = null;
    Object.defineProperty(this, "local_id", {
        set: () => {},
        get: () => local_id
    });
    log.info(`ObjectIndex.ChangeSet.ctor local_id = ${local_id}, remote_id -> undefined`);
    this.remote_id = undefined;
};
let Store = function(context) {
    let pending = Object(), pushing = Object();
    let global_hash = {
        local: null,
        remote: null
    };
    let room = undefined;
    Object.defineProperty(this, "remote_global_hash", {
        set: () => {},
        get: () => {
            if (global_hash.remote === null) {
                return null;
            }
            return global_hash.remote.compute();
        }
    });
    this.init = (new_room) => {
        let old_room = room;
        log.debug(`ObjectIndex.Store.init initializing index room ${String(old_room)} -> ${String(new_room)}`);
        if (String(new_room) === String(old_room)) {
            return Promise.resolve();
        }
        pending = Object();
        room = new_room;
        if (room !== null) {
            global_hash.local = new GlobalHash(room.secret);
            global_hash.remote = new GlobalHash(room.secret);
            return context.object_retrieve({}).modify(object => {
                // AGPOC-6769 : when duplicating sitac do not send Agnet resource creation to server
                if (object.object_data.layer_name === "resources" && object.object_data.attributes.dataMsisdn !== undefined) {
                    // do nothing!
                } else {
                    delete object.remote_id;
                    let changeset = this.commit({
                        local_id: object.local_id,
                        old_data: null,
                        new_data: object.object_data
                    });
                    object.object_hash = changeset.new_hash;
                }
            });
        } else {
            global_hash.local = null;
            global_hash.remote = null;

            return context.object_retrieve({}).each(object => {
                this.commit({
                    local_id: object.local_id,
                    old_data: null,
                    new_data: object.object_data
                });
            });
        }
    };
    this.deinit = () => {
        log.info(`ObjectIndex.Store.deinit  index room ${String(room)} -> null`);
        pending = Object();
        pushing = Object();
        room = null;
        global_hash.local = null;
        global_hash.remote = null;
        return context.object_retrieve({}).modify(object => {
            delete object.remote_id;
            this.commit({
                local_id: object.local_id,
                old_data: null,
                new_data: object.object_data
            });
        });
    };
    this.hash = (object_data) => {
        if (room === null) {
            return null;
        }
        return hash({
            data: object_data,
            secret: room.secret
        });
    };
    this.commit = ({local_id, remote_id, base_remote_id, old_hash, old_data, new_hash, new_data}) => {
        log.debug("ObjectIndex.Store.commit ",{local_id, remote_id, base_remote_id, old_hash, old_data, new_hash, new_data});
        log.debug(`ObjectIndex.Store.commit old_hash = ${old_hash}, new_hash = ${new_hash}`);
        let changeset = null;
        if (remote_id !== undefined) {
            if (old_hash !== null && new_hash !== null) {
                global_hash.local.append_hash(new_hash);
                global_hash.remote.append_hash(new_hash);
                if (local_id in pushing) {
                    // quick update it while we've still got some time!
                    pushing[local_id].old_data = new_data;
                    pushing[local_id].old_hash = new_hash;
                }
                if (local_id in pending) {
                    delete pending[local_id];
                }
            } else if (new_hash !== null) {
                global_hash.local.append_hash(new_hash);
                global_hash.remote.append_hash(new_hash);
            } else if (old_hash !== null) {
                global_hash.local.append_hash(this.hash(""));
                global_hash.remote.append_hash(this.hash(""));
            }
            log.info(`ObjectIndex.Store.commit with remote_id = ${remote_id} old_hash = ${old_hash} new_hash = ${new_hash}, just updating remote global hash = ${global_hash.remote.compute()}`);
            return changeset;
        }
        log.info(`ObjectIndex.Store.commit processing commit local_id = ${local_id} remote_id = ${remote_id} \
                  old_data = ${(old_data===undefined)?"[ ]":"[x]"} new_data = ${(new_data===undefined)?"[ ]":"[x]"}`);
        if (local_id === undefined) {
            throw new Error.build.invalid("changeset local id shall be defined");
        }
        if (old_data === null && new_data === null) {
            throw new Error.build.invalid("at least an old or a new object shall be specified");
        }
        log.debug("ObjectIndex.Store.commit pending = ", pending);
        if (local_id in pending) {
            changeset = pending[local_id];
        } else {
            changeset = new Changeset(local_id);
            pending[local_id] = changeset;
        }
        changeset.remote_id = base_remote_id;
        changeset.local_id = local_id;
        if (old_data !== null && new_data !== null) {
            log.info(`ObjectIndex.Store.commit ${local_id} request updating`);
            changeset.update();
        } else if (new_data !== null) {
            log.info(`ObjectIndex.Store.commit ${local_id} request creating`);
            changeset.store();
        } else if (old_data !== null) {
            log.info(`ObjectIndex.Store.commit ${local_id} request deleting`);
            changeset.delete();
        }
        switch(changeset.method) {
        case OBJECT_CREATE:
            changeset.old_data = "";
            changeset.old_hash = this.hash(changeset.old_data);
            log.info(`ObjectIndex.Store.commit ${local_id} creating old hash = ${changeset.old_hash}`);
            changeset.new_data = deterministic_stringify(new_data);
            changeset.new_hash = this.hash(changeset.new_data);
            log.info(`ObjectIndex.Store.commit ${local_id} creating new hash = ${changeset.new_hash}`);
            break;
        case OBJECT_UPDATE:
            if (local_id in pushing) {
                changeset.old_data = pushing[local_id].new_data;
                changeset.old_hash = pushing[local_id].new_hash;
                log.info(`ObjectIndex.Store.commit update old_hash = ${changeset.old_hash}`);
            } else if (changeset.old_data === null) {
                changeset.old_data = deterministic_stringify(old_data);
                // For Agnet do not recalculate old_hash, use the old_hash saved in database
                log.info("ObjectIndex.Store.commit ", {old_data, new_data});
                if (new_data.attributes.dataMsisdn !== undefined){
                    changeset.old_hash = old_hash;
                // For Users with geolocation do not recalculate old_hash, use the old_hash saved in database
                } else if (old_data.attributes.dataUser !== undefined || new_data.attributes.dataUser !== undefined){
                    changeset.old_hash = old_hash;
                } else {
                    changeset.old_hash = this.hash(changeset.old_data);
                    log.info(`ObjectIndex.Store.commit recompute old_hash = ${changeset.old_hash}`);
                }
                log.info(`ObjectIndex.Store.commit ${local_id} updating old_hash = ${changeset.old_hash}`);
            }
            changeset.new_data = deterministic_stringify(new_data);
            changeset.new_hash = this.hash(changeset.new_data);
            log.info(`ObjectIndex.Store.commit ${local_id} updating ${changeset.old_hash} -> ${changeset.new_hash}`);
            break;
        case OBJECT_DELETE:
            if (changeset.old_data === null) {
                changeset.old_data = deterministic_stringify(old_data);
                changeset.old_hash = this.hash(changeset.old_data);
            }
            changeset.new_data = "";
            changeset.new_hash = this.hash(changeset.new_data);
            log.info(`ObjectIndex.Store.commit ${local_id} deleting ${changeset.old_hash} -> x`);
            break;
        case OBJECT_IGNORE:
            changeset.old_hash = null;
            changeset.old_data = null;
            changeset.new_hash = null;
            changeset.new_data = null;
            break;
        }
        if (changeset.old_hash === changeset.new_hash) {
            log.info(`ObjectIndex.Store.commit ${local_id} this changeset has no effect discarding`);
            delete pending[local_id];
        }
        return changeset;
    };

    const rx_tx_queue = new PQueue({concurrency: 1});

    const push = () => {
        if (room === null) {
            return Promise.resolve();
        }
        log.info("ObjectIndex.push in");

        var changesets = [], updated = [];
        return Promise.resolve().then(() => {
            /*
            if (Number(global_hash.remote.updated) === 0) {
                return pull();
            }
            */
        }).then(() => {
            global_hash.local.state = global_hash.remote.state;
            for (let local_id in pending) {
                const changeset = pending[local_id];
                log.debug(`ObjectIndex.Store.push changeset = ${changeset.local_id} state = ${changeset.state}`);
                if (changeset.state !== CHANGESET_PENDING) {
                    log.debug(`ObjectIndex.Store.push changeset ${changeset.local_id} is not in state pending, ignoring`);
                    continue;
                }
                // recomputing hashes if they has not been computed
                log.debug(`ObjectIndex.Store.push recompute hash before: old_hash = ${changeset.old_hash} new_hash = ${changeset.new_hash}`);
                if (changeset.old_hash === null) {
                    changeset.old_hash = this.hash(changeset.old_data);
                }
                if (changeset.new_hash === null) {
                    changeset.new_hash = this.hash(changeset.new_data);
                }
                log.debug(`ObjectIndex.Store.push recompute hash after: old_hash = ${changeset.old_hash} new_hash = ${changeset.new_hash}`);
                changeset.state = CHANGESET_WAITING;
                log.debug(`ObjectIndex.Store.push changeset ${changeset.local_id} going in state waiting`);
                changesets.push(changeset);
                delete pending[changeset.local_id];
                pushing[changeset.local_id] = changeset;
            }
            if (changesets.length > 0) {
                log.debug(`ObjectIndex.Store.push starting pushing with local global hash ${global_hash.local.compute()}`);
            }
        }).then(() => {
            return changesets.reduce((cursor, changeset) => {
                return cursor.then(() => {
                    changeset.global_hash = global_hash.local.compute();
                    
                    // method_data are the parameters of the request sent to Tacteam server in /mtac/objects
                    let method_data = {
                        space: room.space,
                        desktop: room.desktop,
                        global_hash: changeset.global_hash
                    };
                    switch(changeset.method) {
                    case OBJECT_CREATE:
                    {
                        let new_data = JSON.parse(changeset.new_data);
                        // If the resource is linked to a user, add the user to the request
                        if (new_data.attributes.dataUser !== undefined){
                            log.debug(`ObjectIndex.Store.push OBJECT_CREATE dataUser = ${new_data.attributes.dataUser}`);
                            Object.assign(method_data, {
                                new_hash: changeset.new_hash,
                                object_data: changeset.new_data,
                                user: new_data.attributes.dataUser 
                            });
                        } else {
                            Object.assign(method_data, {
                                new_hash: changeset.new_hash,
                                object_data: changeset.new_data
                            });
                        }
                        log.debug(`ObjectIndex.Store.push pushing changeset ${changeset.local_id} x -> [${changeset.new_hash}]`);
                        break;
                    }
                    case OBJECT_UPDATE:
                    {
                        let data_user;

                        //log.debug("ObjectIndex.Store.push OBJECT_UPDATE ", changeset.old_data);
                        let new_data = JSON.parse(changeset.new_data);
                        let old_data = JSON.parse(changeset.old_data);
                        
                        // is the object data about user ? check new and old data:
                        if (new_data.attributes.dataUser !== undefined) {
                            data_user = new_data.attributes.dataUser; // user is linked to a device
                        } else if (old_data.attributes.dataUser !== undefined) {
                            data_user = old_data.attributes.dataUser; // user is unlinked from a device
                        }

                        if (new_data.attributes.dataMsisdn !== undefined){
                            Object.assign(method_data, {
                                old_hash: changeset.old_hash,
                                new_hash: changeset.new_hash,
                                object_data: changeset.new_data,
                                msisdn: new_data.attributes.dataMsisdn // object is an AGNET user
                            });
                        } else if (data_user !== undefined){
                            log.debug(`ObjectIndex.Store.push OBJECT_UPDATE dataUser = ${data_user}`);
                            Object.assign(method_data, {
                                old_hash: changeset.old_hash,
                                new_hash: changeset.new_hash,
                                object_data: changeset.new_data,
                                user: data_user // object is linked or unlinked to a device
                            });
                        } else {
                            Object.assign(method_data, {
                                old_hash: changeset.old_hash,
                                new_hash: changeset.new_hash,
                                object_data: changeset.new_data
                            });
                        }
                        log.debug(`ObjectIndex.Store.push pushing changeset ${changeset.local_id} [${changeset.old_hash}] -> [${changeset.new_hash}]`);
                        break;
                    }
                    case OBJECT_DELETE:
                    {
                        let old_data = JSON.parse(changeset.old_data);
                        // If the resource was linked to a user, add the user to the request
                        if (old_data.attributes.dataUser !== undefined){
                            log.debug(`ObjectIndex.Store.push OBJECT_DELETE dataUser = ${old_data.attributes.dataUser}`);
                            Object.assign(method_data, {
                                old_hash: changeset.old_hash,
                                user: old_data.attributes.dataUser 
                            });
                        } else {
                            Object.assign(method_data, {
                                old_hash: changeset.old_hash
                            });
                        }
                        log.debug(`ObjectIndex.Store.push pushing changeset ${changeset.local_id} [${changeset.old_hash}] -> x`);
                        break;
                    }}
                    return global.server.mtac_object_update(method_data);
                }).catch(error => {
                    delete pushing[changeset.local_id];
                    changeset.state = CHANGESET_PENDING;
                    switch(changeset.method) {
                    case OBJECT_CREATE:
                        //log.error(`object_index: changeset push failed ${changeset.local_id} x -> [${changeset.new_hash}]`);
                        if (changeset.local_id in pending) {
                            switch(pending[changeset.local_id].method) {
                            case OBJECT_CREATE:
                                log.error(`ObjectIndex.Store.push ${changeset.local_id} merging failed state OBJECT_CREATE -> OBJECT_CREATE`);
                                break;
                            case OBJECT_DELETE:
                                log.warn(`ObjectIndex.Store.push ${changeset.local_id} merging failed state OBJECT_CREATE -> OBJECT_DELETE`);
                                delete pending[changeset.local_id];
                                break;
                            case OBJECT_UPDATE:
                                log.warn(`ObjectIndex.Store.push ${changeset.local_id} merging failed state OBJECT_CREATE -> OBJECT_UPDATE`);
                                changeset.new_hash = pending[changeset.local_id].new_hash;
                                changeset.new_data = pending[changeset.local_id].new_data;
                                pending[changeset.local_id] = changeset;
                                break;
                            }
                        } else {
                            pending[changeset.local_id] = changeset;
                        }
                        break;
                    case OBJECT_UPDATE:
                        log.error(`ObjectIndex.Store.push changset push failed ${changeset.local_id} [${changeset.old_hash}] -> [${changeset.new_hash}] on server`);
                        if (changeset.local_id in pending) {
                            switch(pending[changeset.local_id].method) {
                            case OBJECT_CREATE:
                                log.error(`ObjectIndex.Store.push ${changeset.local_id} merging failed state OBJECT_UPDATE -> OBJECT_CREATE`);
                                break;
                            case OBJECT_DELETE:
                                log.warn(`ObjectIndex.Store.push ${changeset.local_id} merging failed state OBJECT_UPDATE -> OBJECT_DELETE`);
                                break;
                            case OBJECT_UPDATE:
                                log.warn(`ObjectIndex.Store.push ${changeset.local_id} merging failed state OBJECT_UPDATE -> OBJECT_UPDATE`);
                                changeset.new_hash = pending[changeset.local_id].new_hash;
                                changeset.new_data = pending[changeset.local_id].new_data;
                                pending[changeset.local_id] = changeset;
                                break;
                            }
                        } else {
                            pending[changeset.local_id] = changeset;
                        }
                        break;
                    case OBJECT_DELETE:
                        log.error(`ObjectIndex.Store.push changset push failed ${changeset.local_id} [${changeset.old_hash}] -> x`);
                        if (changeset.local_id in pending) {
                            switch(pending[changeset.local_id].method) {
                            case OBJECT_CREATE:
                                log.error(`ObjectIndex.Store.push ${changeset.local_id} merging failed state OBJECT_DELETE -> OBJECT_CREATE`);
                                break;
                            case OBJECT_DELETE:
                                log.error(`ObjectIndex.Store.push ${changeset.local_id} merging failed state OBJECT_DELETE -> OBJECT_DELETE`);
                                break;
                            case OBJECT_UPDATE:
                                log.error(`ObjectIndex.Store.push ${changeset.local_id} merging failed state OBJECT_DELETE -> OBJECT_UPDATE`);
                                break;
                            }
                        } else {
                            pending[changeset.local_id] = changeset;
                        }
                        break;
                    }
                    throw error;
                }).then((response) => {
                    delete pushing[changeset.local_id];
                    changeset.state = CHANGESET_PUSHED;
                    switch(changeset.method) {
                    case OBJECT_CREATE:
                        log.debug(`ObjectIndex.Store.push changeset push success ${changeset.local_id} x -> [${changeset.new_hash}]`);
                        if (changeset.local_id in pending) {
                            switch(pending[changeset.local_id].method) {
                            case OBJECT_CREATE:
                                log.info(`ObjectIndex.Store.push ${changeset.local_id} merging success state OBJECT_CREATE -> OBJECT_CREATE`);
                                break;
                            case OBJECT_DELETE:
                                log.info(`ObjectIndex.Store.push ${changeset.local_id} merging success state OBJECT_CREATE -> OBJECT_DELETE`);
                                pending[changeset.local_id].old_hash = changeset.new_hash;
                                pending[changeset.local_id].old_data = changeset.new_data;
                                break;
                            case OBJECT_UPDATE:
                                log.warn(`ObjectIndex.Store.push ${changeset.local_id} merging success state OBJECT_CREATE -> OBJECT_UPDATE`);
                                pending[changeset.local_id].old_hash = changeset.new_hash;
                                pending[changeset.local_id].old_data = changeset.new_data;
                                break;
                            }
                        }
                        break;
                    case OBJECT_UPDATE:
                        log.debug(`ObjectIndex.Store.push changset push success ${changeset.local_id} [${changeset.old_hash}] -> [${changeset.new_hash}] on server`);
                        if (changeset.local_id in pending) {
                            switch(pending[changeset.local_id].method) {
                            case OBJECT_CREATE:
                                log.info(`ObjectIndex.Store.push ${changeset.local_id} merging success state OBJECT_UPDATE -> OBJECT_CREATE`);
                                break;
                            case OBJECT_DELETE:
                                log.info(`ObjectIndex.Store.push ${changeset.local_id} merging success state OBJECT_UPDATE -> OBJECT_DELETE`);
                                pending[changeset.local_id].old_hash = changeset.new_hash;
                                pending[changeset.local_id].old_data = changeset.new_data;
                                break;
                            case OBJECT_UPDATE:
                                log.info(`ObjectIndex.Store.push ${changeset.local_id} merging success state OBJECT_UPDATE -> OBJECT_UPDATE`);
                                pending[changeset.local_id].old_hash = changeset.new_hash;
                                pending[changeset.local_id].old_data = changeset.new_data;
                                break;
                            }
                        }
                        break;
                    case OBJECT_DELETE:
                        log.debug(`ObjectIndex.Store.push changset push success ${changeset.local_id} [${changeset.old_hash}] -> x`);
                        if (changeset.local_id in pending) {
                            switch(pending[changeset.local_id].method) {
                            case OBJECT_CREATE:
                                log.error(`ObjectIndex.Store.push ${changeset.local_id} merging success state OBJECT_DELETE -> OBJECT_CREATE`);
                                break;
                            case OBJECT_DELETE:
                                log.error(`ObjectIndex.Store.push ${changeset.local_id} merging success state OBJECT_DELETE -> OBJECT_DELETE`);
                                break;
                            case OBJECT_UPDATE:
                                log.error(`ObjectIndex.Store.push ${changeset.local_id} merging success state OBJECT_DELETE -> OBJECT_UPDATE`);
                                break;
                            }
                        }
                        break;
                    }
                    switch(changeset.method) {
                    case OBJECT_CREATE:
                        global_hash.local.append_hash(changeset.new_hash);
                        changeset.remote_id = response.data.remote_id;
                        updated.push(context.update_object_in_database({
                            local_id: changeset.local_id,
                            remote_id: changeset.remote_id,
                            object_hash: changeset.new_hash
                        }));
                        log.debug(`ObjectIndex.Store.push changeset push succeeded ${changeset.local_id} x -> [${changeset.new_hash}]`);
                        break;
                    case OBJECT_UPDATE:
                        global_hash.local.append_hash(changeset.new_hash);
                        changeset.remote_id = response.data.remote_id;
                        updated.push(context.update_object_in_database({
                            local_id: changeset.local_id,
                            remote_id: changeset.remote_id,
                            object_hash: changeset.new_hash
                        }));
                        log.debug(`ObjectIndex.Store.push changeset push succeeded ${changeset.local_id} [${changeset.old_hash}] -> [${changeset.new_hash}]`);
                        break;
                    case OBJECT_DELETE:
                        global_hash.local.append_hash(changeset.new_hash);
                        log.debug(`ObjectIndex.Store.push changeset push succeeded ${changeset.local_id} [${changeset.old_hash}] -> x`);
                        break;
                    }
                    if (response.data.global_hash === global_hash.local.compute()) {
                        log.debug(`ObjectIndex.Store.push updating local global hash ${global_hash.local.compute()}`);
                    } else {
                        // log.error(`new data ${changeset.new_data} new hash ${changeset.new_data} hash of "" ${this.hash("")}`);
                        // log.error(`object_index: failed to match local global hash with remote ${global_hash.local.compute()} != ${response.data.global_hash}`);
                        log.debug("ObjectIndex.Store.push updating remote global hash using pull");
                        global_hash.local.state = global_hash.remote.state;
                        return pull();
                    }
                    global_hash.remote.state = global_hash.local.state;
                    log.debug(`ObjectIndex.Store.push updating remote global hash ${global_hash.remote.compute()}`);
                });
            }, Promise.resolve());
        }).then(() => {
            return Promise.all(updated);
        }).catch(error => {
            switch(error.status) {
            case Error.status.conflict:
                throw error;
            case Error.status.netdown:
            case Error.status.hostdown:
                return;
            case Error.status.access:
                global.storage.unshare();
                return;
            default:
                log.error("ObjectIndex.Store.push failed to handle object update error, will try again next time", error);
                return;
            }
        });
    };
    this.push = () => {
        if (rx_tx_queue.sizeBy({priority: 1}) > 1) {
            return;
        }
        return rx_tx_queue.add(() => push(), {priority: 1});
    };

    const pull = async () => {
        if (room === null) {
            return;
        }
        try {
            log.debug(`ObjectIndex.Store.pull starting pulling with local global hash ${global_hash.local.compute()}`);
            var objects = await global.server.mtac_object_retrieve({
                space: room.space,
                desktop: room.desktop,
                global_hash: global_hash.local.compute()
            });
        } catch(error) {
            switch(error.status) {
            case Error.status.noent:
                log.error(`ObjectIndex.Store.pull unable to pull objects, room space ${room.space} doesn't exist`);
                return;
            case Error.status.netdown:
            case Error.status.hostdown:
                // silently ignoring network down exceptions
                return;
            case Error.status.access:
                global.storage.unshare({});
                return;
            default:
                log.error("ObjectIndex.Store.pull failed to handle object update error, will try again next time", error);
                return;
            }
        }
        
        var {first_global_hash, groups, last_global_hash, last_global_hash_state} = objects.data;
        if (first_global_hash === undefined) {
            // no first global means everything shall be replaced
            pending = {};
            pushing = {};
            await context.object_retrieve({}).modify((_, ref) => {
                delete ref.value;
            });
        }
        /*
        if (groups.length > 0 && Number(global_hash.remote.updated) === 0) {
            // delete database content if something has been pulled
            pending = {};
            pushing = {};
            await context.object_retrieve({}).modify((_, ref) => {
                delete ref.value;
            });
        }
        */
        var updated = [];
        groups.forEach((group, remote_id) => {
            if (group === null) {
                // no changes for this node
            } else if (group.node === null && group.hash === null) {
                // deleted node
                updated.push(context.object_retrieve({remote_id}).modify((object, ref) => {
                    log.debug(`ObjectIndex.Store.pull deleting node ${object.local_id} [${object.object_hash}] -> [x]`);
                    delete ref.value;
                }));
            } else {
                let hash = this.hash(group.node);
                if (hash != group.hash) {
                    throw new Error.build.invalid("ObjectIndex.Store.pull failed to authenticate object hash");
                }
                // updated or added node
                updated.push(context.object_retrieve({remote_id}).modify((object) => {
                    log.debug(`ObjectIndex.Store.pull updating node ${object.local_id} [${object.object_hash}] -> [${hash}]`);
                    if (object.local_id in pushing && pushing[object.local_id].old_hash === hash) {
                        log.warn(`ObjectIndex.Store.pull cancelling pull for a currently pushed object ${object.local_id}`);
                        object.remote_version = Date.now();
                        return;
                    }
                    if (object.local_id in pending && pending[object.local_id].old_hash === hash) {
                        log.warn(`ObjectIndex.Store.pull cancelling pull for a currently pending object ${object.local_id}`);
                        object.remote_version = Date.now();
                        return;
                    }
                    Object.assign(object, {
                        remote_id,
                        remote_version: Date.now(),
                        object_data: JSON.parse(group.node),
                        object_hash: hash
                    });
                }).then(modified => {
                    if (modified > 0) {
                        return;
                    }
                    return context.create_object_in_database({
                        remote_id,
                        remote_version: Date.now(),
                        object_data: JSON.parse(group.node),
                        object_hash: hash
                    }).then(object => {
                        log.debug(`ObjectIndex.Store.pull creating node ${object.local_id} [x] -> [${object.object_hash}]`);
                    });
                }));
            }
        });

        await Promise.all(updated);

        // updating the remote hash
        global_hash.remote.state = last_global_hash_state;
        log.debug(`ObjectIndex.Store.pull updating remote global hash ${last_global_hash}`);
    };

    this.pull = () => {
        if (rx_tx_queue.sizeBy({priority: 0}) > 1) {
            return;
        }
        return rx_tx_queue.add(() => pull(), {priority: 0});
    };

    const revert = async () => {
        var reverted = [];
        log.debug("ObjectIndex.Store.revert in");
        for (let local_id in pending) {
            const changeset = pending[local_id];
            if (changeset.state !== CHANGESET_PENDING) {
                continue;
            }
            changeset.state = CHANGESET_REVERTED;
            switch(changeset.method) {
            case OBJECT_CREATE:
                log.debug(`ObjectIndex.Store.revert reverting changeset ${changeset.local_id} x <- [${changeset.new_hash}]`);
                reverted.push(context.object_revert_create({
                    local_id: changeset.local_id,
                    remote_id: changeset.remote_id
                }));
                break;
            case OBJECT_UPDATE:
                log.debug(`ObjectIndex.Store.revert reverting changeset ${changeset.local_id} [${changeset.old_hash}] <- [${changeset.new_hash}]`);
                reverted.push(context.object_revert_update(changeset));
                break;
            case OBJECT_DELETE:
                log.debug(`ObjectIndex.Store.revert reverting changeset ${changeset.local_id} [${changeset.old_hash}] <- x`);
                reverted.push(context.object_revert_delete({
                    local_id: changeset.local_id,
                    remote_id: changeset.remote_id,
                    object_data: changeset.old_data,
                    object_hash: changeset.old_hash
                }));
                break;
            }
            delete pending[changeset.local_id];
        }
        await Promise.all(reverted);
        await pull();
        log.debug("ObjectIndex.Store.revert out");
    };
    this.revert = () => rx_tx_queue.add(() => revert(), {priority: 2});
};
Object.assign(Changeset.prototype, changeset_prototype);

export default Store;
